import { type AxiosError } from 'axios'
import { makeAutoObservable } from 'mobx'
import { v4 } from 'uuid'

import { DoctorsController } from '@App/api'
import { type IApiErrorHandler } from '@App/types/common'
import {
    type IFeedback,
    type IFeedbackDto,
    type IUpdateFeedback,
    type IDoctorCard,
    type IAdditionalInfo2,
    type IAdditionalInfoNewDto
} from '@App/types/doctorsType'
import { onHandleResponseNotification, onUseQueries } from '@App/utils'
import { IMAGE_BASE_URL } from '@Config/config'

// const TEMPORARY_DOCTOR_ID = '206e5404-6da0-46a3-bf2c-8062feccc9c4'

class Doctor {
    feedbacks: IFeedback[] | null = null
    isFetchingFeedbacks: boolean = false

    doctorCard: IDoctorCard | null = null

    additionalInfo: IAdditionalInfo2[] | null = null
    isFetchingAdditionalInfo: boolean = false

    constructor() {
        makeAutoObservable(this)
    }

    // actions

    onSetIsFetchingFeedbacks(isFetchingFeedbacks: boolean) {
        this.isFetchingFeedbacks = isFetchingFeedbacks
    }

    onSetIsFetchingAdditionalInfo(isFetchingAdditionalInfo: boolean) {
        this.isFetchingAdditionalInfo = isFetchingAdditionalInfo
    }

    onSetDoctorCard(doctorCard: IDoctorCard | null) {
        this.doctorCard = doctorCard
    }

    onSetAdditionalInfo(additionalInfo: IAdditionalInfo2[] | null) {
        this.additionalInfo = additionalInfo
    }

    onSetFeedbacks(feedbacks: IFeedback[] | null) {
        this.feedbacks = feedbacks
    }

    // requests
    async onGetDoctorCard(doctorId: string) {
        const client = new DoctorsController()
        try {
            const { data } = await client.onGetDoctorCard(doctorId)
            this.onSetDoctorCard(data)
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        }
    }

    onGetAdditionalInfoByEmployeeId = async (employeeId: string, onSuccess?: () => void) => {
        this.onSetIsFetchingAdditionalInfo(true)
        const queries = onUseQueries({ employeeId })
        const client = new DoctorsController()
        try {
            const { data } = await client.onGetAdditionalInfoByEmployeeId(queries)
            this.onSetAdditionalInfo(
                data.additionalInfo.map((el: IAdditionalInfo2) => ({
                    ...el,
                    items: el.items.map((item) => ({ id: v4(), title: item }))
                }))
            )
            onSuccess?.()
            // this.onSetDoctorCard(data)
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        } finally {
            this.onSetIsFetchingAdditionalInfo(false)
        }
    }

    onUpdateAdditionalInfo = async (dto: IAdditionalInfoNewDto, onSuccess?: () => void) => {
        this.onSetIsFetchingAdditionalInfo(true)
        const client = new DoctorsController()
        try {
            const { status } = await client.onUpdateAdditionalInfo(dto)
            if (status >= 200 && status < 300) {
                this.onSetAdditionalInfo(dto.additionalInfo)
                onSuccess?.()
            }
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        } finally {
            this.onSetIsFetchingAdditionalInfo(false)
        }
    }

    async onUpdateDoctorCard(doctorId: string, dto: IDoctorCard, onSuccess: () => void) {
        const client = new DoctorsController()
        try {
            const { status } = await client.onUpdateDoctorCard(doctorId, dto)
            if (status === 200) {
                onSuccess()
            }
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        }
    }

    async onGetFeedbacksById(doctorId: string, isPublished: boolean) {
        const client = new DoctorsController()
        const queries = onUseQueries({
            isPublished: isPublished ? 'true' : 'false',
            doctorId
        })
        try {
            const { data } = await client.onGetFeedbacksById(queries)
            this.onSetFeedbacks(data.value.sort((a: IFeedback, b: IFeedback) => (a.createdAt > b.createdAt ? -1 : 1)))
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        }
    }

    async onAddFeedback(dto: IFeedbackDto, onSuccess: () => void) {
        this.onSetIsFetchingFeedbacks(true)
        const client = new DoctorsController()
        try {
            const { status } = await client.onAddFeedback(dto)
            onHandleResponseNotification(status, `Отзыв успешно добавлен`)
            this.onSetFeedbacks(null)
            this.onGetFeedbacksById(dto.doctorId, false)
            onSuccess()
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        } finally {
            this.onSetIsFetchingFeedbacks(false)
        }
    }

    async onUpdateFeedback(id: string, dto: IUpdateFeedback, onSuccess?: () => void) {
        this.onSetIsFetchingFeedbacks(true)
        const client = new DoctorsController()
        try {
            const { status } = await client.onUpdateFeedback(dto)
            this.onSetFeedbacks(null)
            this.onGetFeedbacksById(id, dto.isPublished)
            onHandleResponseNotification(status, `Отзыв успешно изменен`)
            onSuccess?.()
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        } finally {
            this.onSetIsFetchingFeedbacks(false)
        }
    }

    async onRemoveFeedback(id: string, feedbackId: string, isPublished: boolean, onSuccess?: () => void) {
        this.onSetIsFetchingFeedbacks(true)
        const client = new DoctorsController()
        const queries = onUseQueries({ id: feedbackId })
        try {
            const { status } = await client.onRemoveFeedback(queries)
            this.onSetFeedbacks(null)
            this.onGetFeedbacksById(id, isPublished)
            onHandleResponseNotification(status, `Отзыв успешно удален`)
            onSuccess?.()
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        } finally {
            this.onSetIsFetchingFeedbacks(false)
        }
    }

    async onUploadImage(id: string, file: Blob, onSuccess: () => void, updateDto: IDoctorCard) {
        const client = new DoctorsController()
        const dto = new FormData()
        dto.append('file', file)
        const queries = onUseQueries({ kind: 'doctor' })
        try {
            const { data } = await client.onUploadImage(dto, queries)
            if (data.location) {
                this.onUpdateDoctorCard(id, { ...updateDto, avatar: `${IMAGE_BASE_URL}${data.location}` }, onSuccess)
            }
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        }
    }

    async onChangeImage(file: Blob, fileName: string, onSuccess: () => void) {
        const client = new DoctorsController()
        const queries = onUseQueries({ kind: 'doctor' })
        const dto = new FormData()
        dto.append('file', file)
        try {
            const { data } = await client.onChangeImage(dto, fileName, queries)
            if (data.location) {
                onSuccess()
            }
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        }
    }

    async onRemoveImage(doctorId: string, imageName: string, updateDto: IDoctorCard, onSuccess: () => void) {
        const client = new DoctorsController()
        const queries = onUseQueries({ kind: 'doctor' })
        const _imageName = imageName.substring(imageName.lastIndexOf('/') + 1)
        try {
            const { status } = await client.onRemoveImage(_imageName, queries)
            if (status === 204) {
                this.onUpdateDoctorCard(doctorId, { ...updateDto, avatar: null }, onSuccess)
            }
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        }
    }
}

export default new Doctor()
